import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { useAuth } from '../../hooks/useAuth';
import BannerBackground from '@/components/common/BannerBackground';
import logoDark from '@/assets/images/logo-dark.png';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [formData, setFormData] = useState({
    emailOrUsername: '',
    password: ''
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [loginError, setLoginError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lockoutSeconds, setLockoutSeconds] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Get the intended destination or default to home
  const from = location.state?.from?.pathname || '/';

  // Check for session expired message
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('sessionExpired')) {
      setSessionExpired(true);
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location]);

  // Countdown timer for account lockout
  useEffect(() => {
    if (lockoutSeconds === null || lockoutSeconds <= 0) return;

    const timer = setInterval(() => {
      setLockoutSeconds(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setLoginError('');
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [lockoutSeconds]);

  // Format seconds into MM:SS display
  const formatCountdown = useCallback((totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  const isLockedOut = lockoutSeconds !== null && lockoutSeconds > 0;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Clear global error
    if (loginError) {
      setLoginError('');
    }
  };

  const validateForm = () => {
    const errors = {};

    // Check if emailOrUsername is provided
    if (!formData.emailOrUsername.trim()) {
      errors.emailOrUsername = 'Email or username is required';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare the login data with autodetection
      const loginPayload = {
        email: formData.emailOrUsername.includes('@') ? formData.emailOrUsername : '',
        username: !formData.emailOrUsername.includes('@') ? formData.emailOrUsername : '',
        password: formData.password
      };

      const result = await login(loginPayload);

      // Check for MustChangePassword response FIRST (before success check)
      if (result.mustChangePassword && result.passwordResetToken) {
        const email = encodeURIComponent(result.email || formData.emailOrUsername);
        const token = encodeURIComponent(result.passwordResetToken);
        navigate(`/reset-password?token=${token}&email=${email}&mustChange=true`, { replace: true });
        return;
      }

      if (result.success) {
        // Handle different login outcomes based on store access
        switch (result.nextStep) {
          case 'home':
            // Single store/company or already selected - go to homepage
            navigate(from, { replace: true });
            break;
          case 'select_store':
            // Multiple stores - go to account dashboard for store selection
            navigate('/account', { replace: true });
            break;
          case 'select_company':
            // Employee with multiple companies - go to account dashboard for company selection
            navigate('/account', { replace: true });
            break;
          case 'no_stores':
            // No store/company access
            if (result.isEmployeePreview) {
              setLoginError('Your employee account does not have access to any companies. Please contact your administrator.');
            } else {
              setLoginError('Your account does not have access to any stores. Please contact your sales representative.');
            }
            break;
          default:
            // Default case - go to intended page
            navigate(from, { replace: true });
        }
      } else {
        setIsSubmitting(false);

        // Fallback check for must change password
        if (result.mustChangePassword) {
          if (result.passwordResetToken) {
            const email = encodeURIComponent(result.email || formData.emailOrUsername);
            const token = encodeURIComponent(result.passwordResetToken);
            navigate(`/reset-password?token=${token}&email=${email}&mustChange=true`, { replace: true });
            return;
          } else {
            setLoginError('Your password has expired and must be changed. Please use the "Forgot Password" link below or contact your sales representative.');
            return;
          }
        }

        // Handle login errors
        if (result.statusCode === 423 && result.remainingMinutes) {
          // Account is locked - start countdown timer
          const seconds = Math.ceil(result.remainingMinutes * 60);
          setLockoutSeconds(seconds);
          setLoginError('Account is temporarily locked due to too many failed login attempts.');
        } else if (result.requiresProfileConfirmation) {
          setLoginError('Your profile needs to be confirmed. Please check your email or contact sales representative.');
        } else if (result.isInvalidCredentials) {
          setLoginError('Invalid email/username or password. Please try again.');
        } else if (result.isAccountInactive) {
          setLoginError('Your account is not active. Please contact your sales representative.');
        } else {
          setLoginError(result.message || 'Login failed. Please try again.');
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      setIsSubmitting(false);
      setLoginError('An unexpected error occurred. Please try again.');
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center bg-light">
      <BannerBackground/>
      <Container className="position-relative">
        <Row className="justify-content-center">
          <Col md={6} lg={5} xl={4}>
            <Card className="shadow border-0">
              <Card.Body className="p-5">
                {/* Logo/Brand */}
                <div className="text-center mb-4">
                  <img 
                      src={logoDark}
                      alt="Stash House"
                      className="stash-header__logo-img img-fluid mb-3"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'inline';
                      }}
                  />
                  <p className="text-muted">Sign in to your account</p>
                </div>

                {/* Session Expired Message */}
                {sessionExpired && (
                  <Alert variant="warning" className="mb-4 border-warning d-flex align-items-center" dismissible onClose={() => setSessionExpired(false)}>
                    <i className="bi bi-clock-history me-3 fs-4 text-warning"></i>
                    <div>
                      <strong>Session Expired</strong>
                      <div className="small text-muted">Your session has expired. Please log in again.</div>
                    </div>
                  </Alert>
                )}

                {/* Error Alert */}
                {loginError && (
                  <Alert variant="danger" className="mb-4" dismissible={!isLockedOut} onClose={() => !isLockedOut && setLoginError('')}>
                    {loginError}
                    {isLockedOut && (
                      <div className="mt-2 text-center">
                        <strong>Try again in: </strong>
                        <span className="font-monospace fs-5">{formatCountdown(lockoutSeconds)}</span>
                      </div>
                    )}
                  </Alert>
                )}

                {/* Login Form */}
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email or Username</Form.Label>
                    <Form.Control
                      type="text"
                      name="emailOrUsername"
                      value={formData.emailOrUsername}
                      onChange={handleInputChange}
                      isInvalid={!!validationErrors.emailOrUsername}
                      placeholder="Enter your email or username"
                      disabled={isSubmitting || isLockedOut}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.emailOrUsername}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Password</Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        isInvalid={!!validationErrors.password}
                        placeholder="Enter your password"
                        disabled={isSubmitting || isLockedOut}
                        style={{ paddingRight: '2.5rem' }}
                      />
                      <Button
                        variant="link"
                        className="position-absolute top-50 end-0 translate-middle-y text-muted"
                        style={{ zIndex: 10, padding: '0.375rem 0.75rem' }}
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={isSubmitting || isLockedOut}
                        type="button"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                      </Button>
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.password}
                      </Form.Control.Feedback>
                    </div>
                  </Form.Group>

                  <div className="d-flex justify-content-end mb-4">
                    <Link to="/forgot-password" className="text-decoration-none small">
                      Forgot your password?
                    </Link>
                  </div>

                  <div className="d-grid mb-3">
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      disabled={isSubmitting || isLockedOut}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Signing in...
                        </>
                      ) : (
                        'Sign In'
                      )}
                    </Button>
                  </div>

                  <div className="text-center">
                    <small className="text-muted">
                      Don't have an account?{' '}
                      <a
                        href={`${import.meta.env.VITE_APP_PUBLIC_URL.replace(/\/$/, '')}/register`}
                        className="text-decoration-none"
                      >
                        Create one here
                      </a>
                    </small>
                  </div>
                </Form>
              </Card.Body>
            </Card>

            {/* Additional Info */}
            <div className="text-center mt-4">
              <small className="text-muted">
                Need help? Contact your sales representative or email us at <a href="mailto:info@stashhousedistro.com">info@stashhousedistro.com</a>
                {/* or{' '}
                <Link to="/support" className="text-decoration-none">
                  visit our support page
                </Link> */}
              </small>
            </div>

            {/* Store Selection Info */}
            <div className="text-center mt-3">
              <small className="text-muted">
                <i className="bi bi-info-circle me-1"></i>
                After login, you may need to select a store to begin shopping
              </small>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default LoginPage;